import {
    ChangeDetectionStrategy,
    Component,
    Injectable,
    Input,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Setting } from '@app-state';
import {
    IFolder,
    IFolderAssignedUser,
    IFolderAssignment,
    IFolderUserRole,
    ODataResponse,
    RoleOrigin,
    RolesAssignment,
    UserType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { AssignUserModalComponent } from '@pages/folders/modals';
import {
    GRID_SERVICE,
    IGridSource,
    ODataGridComponent,
} from '@pages/generics/grid';
import {
    FoldersService,
    MapperService,
    ODataQueryOptions,
} from '@services';
import { addToObjectIf } from '@utils/object.utils';

import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

interface IGridFolderAssignment extends IFolderAssignment {
    rolesInheritance?: string;
}

@Injectable()
export class FolderUsersSource implements IGridSource<ODataQueryOptions, ODataResponse<IFolderAssignment>> {

    public folderId?: number;
    public includeInheritedUsers = false;

    public get mapper() {
        return MapperService.folderAssignmentsMap;
    }

    constructor(
        private _foldersService: FoldersService,
    ) { }

    public get(queryOptions: ODataQueryOptions) {
        return this._foldersService.getAssignedUsers(this.folderId!, this.includeInheritedUsers, queryOptions);
    }
}

@Component({
    selector: 'ui-folder-users-grid',
    templateUrl: './folder-users.grid.html',
    styleUrls: ['./folder-users.grid.scss'],
    providers: [
        FolderUsersSource,
        {
            provide: GRID_SERVICE,
            useExisting: FolderUsersSource,
            deps: [FoldersService],
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class FolderUsersGridComponent
    extends ODataGridComponent<IGridFolderAssignment, number>
    implements OnInit {

    public readonly RolesAssignment = RolesAssignment;
    public readonly UserType = UserType;
    public readonly isWindowsAuthenticationEnabled = this._appState.settings.get(Setting.isWindowsAuthEnabled);

    public processedData$!: Observable<IGridFolderAssignment[]>;
    public hasPermissionsToOpenMenu$!: Observable<boolean>;

    @Input()
    public get folder(): IFolder {
        return this._folder$.value as IFolder;
    }
    public set folder(folder: IFolder) {
        const svc = this._service as unknown as FolderUsersSource;
        svc.folderId = folder.id;
        this._folder$.next(folder);
        this._refresh$.next();
    }

    public get includeInheritedUsers() {
        return this._includeInheritedUsers;
    }
    public set includeInheritedUsers(value) {
        const svc = this._service as unknown as FolderUsersSource;
        this._includeInheritedUsers = svc.includeInheritedUsers = value;
        this._refresh$.next();
    }

    private _folder$ = new BehaviorSubject<IFolder | null>(null);
    private _includeInheritedUsers = false;
    private _foldersService = this._injector.get<FoldersService>(FoldersService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);

    ngOnInit() {
        this.processedData$ = this.data$
            .pipe(
                map((data) =>
                    data.map(entry => {
                        entry.rolesInheritance = this._getRolesDetails(entry.user, entry.roles);
                        return entry;
                    }),
                ),
                takeUntil(this._destroyed$),
            );

        this._appState.language.change$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => this._refresh$.next());

        this.hasPermissionsToOpenMenu$ = this._folder$.pipe(
            switchMap(folder => this._appState.user.folderPermissions
                .isGranted(folder!.id, 'Units.Edit | SubFolders.Edit')),
        );
    }

    public openAssignUserModal(entry?: IFolderAssignment, isLocalUser = true) {
        this._modal
            .openModal(AssignUserModalComponent, {
                folder: this.folder,
                ...addToObjectIf<any>(
                    !!entry,
                    { assignment: entry },
                ),
            }, { data: { isLocalUser } })
            .afterClosedResult()
            .subscribe(result => result && this._refresh$.next());
    }

    public unassignUser(entry: IFolderAssignment) {
        this._modal.onConfirm(
            null,
            () => {
                this._foldersService
                    .removeUserFromFolder(this._folder$.value!.id, entry.user.id)
                    .subscribe(() => this._refresh$.next());
            },
            () => { },
            this._translateService.instant('folder_users_grid_are_you_sure_unassign'),
        );
    }

    private _getRoleOrigin(user: IFolderAssignedUser, role: IFolderUserRole) {
        return role.origin === RoleOrigin.Assigned
            ? user.isInherited
                ? ` (${this._translateService.instant('folder_users_grid_inherited_from_tenant')})`
                : ''
            : ` (${this._translateService.instant('folder_users_grid_inherited_from', { folder: role.inheritedFromFolder!.displayName })})`;
    }

    private _getRolesDetails(user: IFolderAssignedUser, roles: IFolderUserRole[]) {
        return roles
            .map(role => `${role.name}${this._getRoleOrigin(user, role)}`)
            .join(', ');
    }
}
